import { X, Clock, Calendar, Zap, Repeat, Users } from 'lucide-react';
import { DraftResumeBanner } from '../UnsavedChangesDialog';

/**
 * Pure presentational form for the Follow-up flow. All state + side-effects
 * live in `FollowUpModal`; this component only renders inputs and forwards
 * change events.
 */

const PRIORITIES = ['URGENT', 'HIGH', 'MEDIUM', 'LOW'];
const ENERGY_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];
const RECURRENCE_RULES = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY'];
const PRIORITY_COLORS = {
  URGENT: 'var(--pd-crimson)', HIGH: 'var(--pd-warning)',
  MEDIUM: 'var(--pd-focus)', LOW: 'var(--pd-subtle)',
};
const SCHEDULE_OPTIONS = [
  ['today', 'Today'], ['today-time', 'Today at time'],
  ['tomorrow', 'Tomorrow'], ['pick-datetime', 'Pick date & time'],
];

const labelStyle = { color: 'var(--pd-subtle)' };
const inputStyle = { border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)' };
const pillBase = 'flex-1 py-2.5 rounded-xl text-[11px] font-medium transition-colors';

function PillToggle({ active, onClick, label, testid, activeBg = 'var(--pd-focus)', activeColor = 'white' }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={pillBase}
      style={{
        background: active ? activeBg : 'var(--pd-panel-2)',
        color: active ? activeColor : 'var(--pd-muted)',
      }}
    >
      {label}
    </button>
  );
}

export function FollowUpForm({
  form, setForm, businesses, typeLabel,
  hasDraft, restoreDraft, clearDraft,
  todayDate, tomorrowDate,
  onSubmit, guardedClose,
}) {
  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <form onSubmit={onSubmit} className="p-6 space-y-4">
      {hasDraft && (
        <DraftResumeBanner draftKey="followup" onRestore={() => { restoreDraft(); clearDraft(); }} onDiscard={clearDraft} />
      )}
      <input
        data-testid="followup-title-input"
        autoFocus
        value={form.title}
        onChange={(e) => update({ title: e.target.value })}
        placeholder="Follow-up task title"
        className="w-full text-2xl font-medium outline-none bg-transparent"
        style={{ color: 'var(--pd-text)', letterSpacing: '-0.04em' }}
      />
      <textarea
        data-testid="followup-description-input"
        value={form.description}
        onChange={(e) => update({ description: e.target.value })}
        placeholder="Add description..."
        rows={2}
        className="w-full text-sm outline-none resize-none rounded-[18px] px-4 py-3"
        style={{ background: 'var(--pd-panel-solid)', border: '1px solid var(--pd-border)', color: 'var(--pd-muted)' }}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}>Business</label>
          <select data-testid="followup-business-select" value={form.businessId} onChange={(e) => update({ businessId: e.target.value })} className="w-full px-3 py-3 rounded-[16px] text-sm" style={inputStyle}>
            <option value="">None</option>
            {businesses.map((biz) => <option key={biz.id} value={biz.id}>{biz.shortCode} - {biz.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}>Priority</label>
          <div className="flex gap-1.5">
            {PRIORITIES.map((p) => (
              <PillToggle
                key={p}
                active={form.priority === p}
                onClick={() => update({ priority: p })}
                label={p.slice(0, 3)}
                activeBg={PRIORITY_COLORS[p]}
              />
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}>When should this appear?</label>
        <div className="flex flex-wrap gap-1.5">
          {SCHEDULE_OPTIONS.map(([key, label]) => (
            <PillToggle
              key={key}
              testid={`followup-schedule-${key}`}
              active={form.schedulePreference === key}
              onClick={() => {
                const scheduledDateTime = key === 'tomorrow' ? `${tomorrowDate}T09:00`
                  : key === 'today-time' ? `${todayDate}T09:00`
                  : form.scheduledDateTime;
                update({ schedulePreference: key, scheduledDateTime });
              }}
              label={label}
            />
          ))}
        </div>
        {(form.schedulePreference === 'today-time' || form.schedulePreference === 'pick-datetime') && (
          <input data-testid="followup-scheduled-datetime" type="datetime-local" value={form.scheduledDateTime} onChange={(e) => update({ scheduledDateTime: e.target.value })} className="w-full mt-3 px-3 py-3 rounded-[16px] text-sm" style={inputStyle} />
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}><Clock className="w-3 h-3 inline mr-1" />Estimate</label>
          <div className="flex gap-1.5">
            {[15, 30, 45, 60, 90].map((m) => (
              <PillToggle key={m} active={form.estimatedMinutes === m} onClick={() => update({ estimatedMinutes: m })} label={`${m}m`} />
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}><Calendar className="w-3 h-3 inline mr-1" />Due date</label>
          <input data-testid="followup-due-date" type="date" value={form.dueDate} onChange={(e) => update({ dueDate: e.target.value })} className="w-full px-3 py-3 rounded-[16px] text-sm" style={inputStyle} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}><Zap className="w-3 h-3 inline mr-1" />Energy</label>
          <div className="flex gap-1.5">
            {ENERGY_LEVELS.map((e) => (
              <button
                key={e} type="button" onClick={() => update({ energy: e })}
                className="flex-1 py-2.5 rounded-xl text-[11px] font-medium transition-colors"
                style={{
                  background: form.energy === e ? 'var(--pd-panel)' : 'transparent',
                  color: form.energy === e ? 'var(--pd-text)' : 'var(--pd-subtle)',
                  border: form.energy === e ? '1px solid var(--pd-border)' : '1px solid transparent',
                }}
              >{e}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}><Repeat className="w-3 h-3 inline mr-1" />Recurrence</label>
          <div className="flex gap-1.5">
            {RECURRENCE_RULES.map((r) => (
              <PillToggle
                key={r}
                active={form.recurrenceRule === r}
                onClick={() => update({ recurrenceRule: r })}
                label={r === 'NONE' ? 'Once' : r.charAt(0) + r.slice(1).toLowerCase()}
              />
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={labelStyle}><Users className="w-3 h-3 inline mr-1" />Delegate to</label>
        <input data-testid="followup-delegate-input" value={form.delegatedTo} onChange={(e) => update({ delegatedTo: e.target.value })} placeholder="Name or email" className="w-full px-3 py-2.5 rounded-[16px] text-sm" style={{ ...inputStyle, color: 'var(--pd-text)' }} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" data-testid="followup-cancel-btn" onClick={guardedClose} className="px-4 py-2 rounded-[14px] text-sm font-medium" style={{ color: 'var(--pd-muted)' }}>Skip</button>
        <button data-testid="followup-create-btn" type="submit" className="px-5 py-2.5 rounded-[14px] text-sm font-medium text-white btn-primary">Create follow-up</button>
      </div>
    </form>
  );
}

export { X };
